import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AgGridTable, Pagination, DateRangePicker, GroupedCheckboxDropdown } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { FaSearch, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa6';
import { MdFilterAlt, MdDescription } from 'react-icons/md';
import { getPIFs, exportPIFs } from 'redux/crm/report/reportSlice';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import AdvanceFilterDrawer, { getEmptyValues } from 'components/common/AdvanceFilterDrawer';
import SalesUnitGroupedDropdown from 'components/app/crm/shared/SalesUnitGroupedDropdown';
import DynamicFilters, { buildDynamicPayload, buildDynamicTags, dynamicRowTagId } from 'components/app/crm/reports/pif/DynamicFilters';
import { debounce } from 'helpers/debounce';
import NumberRenderer from 'components/common/ag-grid/NumberRenderer';
import PIFAreaTable from 'components/app/crm/reports/pif/PIFAreaTable';

// ── Constants ─────────────────────────────────────────────────────────────────

const SEARCH_FIELD = 'project_q_number_or_project_j_number_or_project_name_cont';

// PIFs accumulate over time — default to the whole current year (matches the
// legacy PIF report's startOfYear default) so older PIFs aren't hidden.
const YEAR_START = dayjs().startOf('year').format('YYYY-MM-DD');
const TODAY      = dayjs().format('YYYY-MM-DD');

// adaptation.type_revision enum [revision, change_order, enter_job]. Business
// relabel for this report: revision → Quotation, change_order → Enter job.
const PIF_TYPE_LABELS = { revision: 'Quotation', change_order: 'Enter job', enter_job: 'Enter job' };

const PIF_TYPE_GROUPS = [{
  key:   'pif_type',
  label: 'PIF Type',
  color: '#8b5cf6',
  items: [
    { value: 0, label: 'Quotation' },   // type_revision = revision
    { value: 1, label: 'Enter job' },   // type_revision = change_order
  ],
}];

// ── Column definitions ────────────────────────────────────────────────────────

const COLUMN_DEFS = [
  {
    headerName:   'PIF no.',
    colId:        'id',
    minWidth:     80,
    width:        100,
    flex:         0,
    pinned:       'left',
    sortable:     true,
    valueGetter:  (p) => p.data?.id,
    cellRenderer: (p) => {
      const { project, building, adaptation, id } = p.data || {};
      if (!project || !building || !adaptation) return id;
      return (
        <Link
          to={`/crm/projects/${project.id}/buildings/${building.id}/adaptations/${adaptation.id}/pifs/${id}`}
          target="_blank"
          className="tw-text-primary-600 hover:tw-underline tw-font-medium"
        >
          {id}
        </Link>
      );
    },
  },
  {
    headerName:   'Quote number',
    colId:        'project_q_number',
    minWidth:     130,
    width:        140,
    flex:         0,
    pinned:       'left',
    valueGetter:  (p) => p.data?.project?.q_number,
    cellRenderer: (p) => {
      const proj = p.data?.project;
      if (!proj) return null;
      return <Link to={`/crm/projects/${proj.id}`} className="tw-text-yellow-600 hover:tw-underline tw-font-medium">{proj.q_number}</Link>;
    },
  },
  {
    headerName:   'P/J number',
    colId:        'building_j_number',
    minWidth:     120,
    valueGetter:  (p) => p.data?.building?.j_number,
    cellRenderer: (p) => <span className="tw-text-amber-600 tw-font-medium">{p.value}</span>,
  },
  {
    headerName:   'Project name',
    colId:        'project_name',
    minWidth:     200,
    valueGetter:  (p) => p.data?.project?.name,
    cellRenderer: (p) => {
      const proj = p.data?.project;
      if (!proj) return null;
      return <Link to={`/crm/projects/${proj.id}`} className="tw-text-blue-600 hover:tw-underline">{proj.name}</Link>;
    },
  },
  {
    headerName:   'Customer name',
    colId:        'customer_name',
    minWidth:     200,
    valueGetter:  (p) => p.data?.project?.customer_name,
    cellRenderer: (p) => {
      const proj = p.data?.project;
      if (!proj?.customer_name) return null;
      return <Link to={`/crm/customers/${proj.customer_id}`} className="tw-text-blue-600 hover:tw-underline">{proj.customer_name}</Link>;
    },
  },
  {
    headerName:   'PIF Type',
    colId:        'pif_type',
    minWidth:     110,
    valueGetter:  (p) => PIF_TYPE_LABELS[p.data?.adaptation?.type] ?? p.data?.adaptation?.type,
    cellRenderer: (p) => {
      if (!p.value) return null;
      const isQuotation = p.value === 'Quotation';
      return (
        <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-md tw-text-[11px] tw-font-semibold ${isQuotation ? 'tw-bg-sky-50 tw-text-sky-700 tw-border tw-border-sky-200' : 'tw-bg-violet-50 tw-text-violet-700 tw-border tw-border-violet-200'}`}>
          {p.value}
        </span>
      );
    },
  },
  { headerName: 'Building', colId: 'building_name', minWidth: 110, valueGetter: (p) => p.data?.building?.name },
  { headerName: 'REV#',     colId: 'adaptation_rev', minWidth: 90, valueGetter: (p) => p.data?.adaptation?.rev },
  { headerName: 'CO#',      colId: 'adaptation_co',  minWidth: 90, valueGetter: (p) => p.data?.adaptation?.co  },
  { headerName: 'Weight (M.T)', colId: 'building_weight', minWidth: 120, cellRenderer: NumberRenderer, valueGetter: (p) => p.data?.building?.weight },
  {
    headerName:  'Areas',
    colId:       'areas',
    minWidth:    900,
    autoHeight:  true,
    wrapText:    true,
    cellStyle:   { paddingTop: 6, paddingBottom: 6, lineHeight: 1.5 },
    // Cap the cell height (internal scroll) so a PIF with many areas can't
    // produce a 2000px row — that broke AG Grid's virtualization at scale and
    // left the grid rendering blank. Bounded rows keep virtualization stable.
    cellRenderer: (p) => (
      <div className="tw-max-h-[360px] tw-overflow-y-auto tw-w-full tw-pr-1">
        <PIFAreaTable data={p.data?.areas} />
      </div>
    ),
  },
  { headerName: 'Location', colId: 'building_location', minWidth: 200, valueGetter: (p) => p.data?.building?.pj_location },
  { headerName: 'Sales in-charge', colId: 'project_sales_incharge', minWidth: 120, valueGetter: (p) => p.data?.project?.sales_incharge },
  { headerName: 'Sales unit',     colId: 'sales_unit_description', minWidth: 130, valueGetter: (p) => p.data?.sales_unit?.description },
  { headerName: 'Created by',   colId: 'created_by',   minWidth: 120, valueGetter: (p) => p.data?.created_by },
  { headerName: 'Created date', colId: 'created_at', sortable: true, minWidth: 160, valueGetter: (p) => p.data?.created_date },
];

// ── Advanced filter drawer fields ─────────────────────────────────────────────
// The fixed fields were removed in favour of the flexible "Precise conditions"
// builder (record + Area/BDS values), which supersedes them.

const FILTER_FIELDS = [];

const DIVIDER_AFTER = [];

// ── Component ─────────────────────────────────────────────────────────────────

const PIFPage = () => {
  const dispatch = useDispatch();
  const {
    piffData: { data = [], totalRows = 0, totalPagesCount = 1, exporting = false },
  } = useSelector((s) => s.crm_report);
  const { activeSalesUnitsData = [] } = useSelector((s) => s.crm_sales_unit);

  const [page, setPage]                 = useState(1);
  const [perPage, setPerPage]           = useState(50);
  const [sort, setSort]                 = useState({ attribute: 'id', direction: 'desc' });
  const [search, setSearch]             = useState('');
  const [filterValues, setFilterValues] = useState(() => getEmptyValues(FILTER_FIELDS));
  const [filterTags, setFilterTags]     = useState([]);
  const [showFilter, setShowFilter]     = useState(false);
  const [loading, setLoading]           = useState(false);

  // Quick filters
  const [quickCreatedDate, setQuickCreatedDate] = useState({ startDate: YEAR_START, endDate: TODAY });
  const [quickSalesUnitIds, setQuickSalesUnitIds] = useState([]);
  const [quickPifTypeIds, setQuickPifTypeIds]     = useState([]);
  const [dynRows, setDynRows]                     = useState([]); // precise-condition builder rows (inside More Filters)

  // Always-fresh refs
  const searchRef             = useRef('');
  const activeFilterParamsRef = useRef({});
  const sortRef               = useRef(sort);
  const perPageRef            = useRef(perPage);
  const pageRef               = useRef(page);
  const quickCreatedRef       = useRef(quickCreatedDate);
  const quickUnitsRef         = useRef([]);
  const quickPifTypeRef       = useRef([]);
  const dynamicPayloadRef     = useRef({ q: {}, area_conditions: [] }); // precise (dynamic) filters: ransack q + area JSONB conditions

  sortRef.current         = sort;
  perPageRef.current      = perPage;
  pageRef.current         = page;
  quickCreatedRef.current = quickCreatedDate;
  quickUnitsRef.current   = quickSalesUnitIds;
  quickPifTypeRef.current = quickPifTypeIds;

  const totalPages = totalPagesCount || Math.ceil(totalRows / perPage) || 1;

  useEffect(() => { dispatch(getActiveSalesUnits()); }, [dispatch]);

  // ── Param builders ────────────────────────────────────────────────

  const buildCreatedDateParam = useCallback((range) => {
    const p = {};
    if (range.startDate) p.pif_created_at_gteq = range.startDate;
    if (range.endDate)   p.pif_created_at_lteq = range.endDate;
    return p;
  }, []);

  const buildUnitsParam   = useCallback((ids) => (ids.length ? { sales_unit_id_in: ids } : {}), []);
  const buildPifTypeParam = useCallback((ids) => (ids.length ? { adaptation_type_revision_in: ids } : {}), []);

  const buildBase = useCallback((overrides = {}) => ({
    sort:     sortRef.current,
    per_page: perPageRef.current,
    page:     pageRef.current,
    ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}),
    ...activeFilterParamsRef.current,
    ...overrides,
  }), []);

  const buildAllParams = useCallback((overrides = {}) => ({
    ...buildBase(overrides),
    ...buildCreatedDateParam(quickCreatedRef.current),
    ...buildUnitsParam(quickUnitsRef.current),
    ...buildPifTypeParam(quickPifTypeRef.current),
    ...(Object.keys(dynamicPayloadRef.current.q).length ? { q: dynamicPayloadRef.current.q } : {}),
    ...(dynamicPayloadRef.current.area_conditions.length ? { area_conditions: JSON.stringify(dynamicPayloadRef.current.area_conditions) } : {}),
    ...overrides,
  }), [buildBase, buildCreatedDateParam, buildUnitsParam, buildPifTypeParam]);

  // ── Fetch ─────────────────────────────────────────────────────────

  const fetchData = useCallback(async (params) => {
    setLoading(true);
    try { await dispatch(getPIFs(params)); } finally { setLoading(false); }
  }, [dispatch]);

  useEffect(() => {
    fetchData(buildAllParams({ page, per_page: perPage, sort }));
  }, [page, perPage, sort, fetchData, buildAllParams]);

  const debouncedFetch = useRef(debounce((params) => fetchData(params), 500)).current;

  // ── Handlers ──────────────────────────────────────────────────────

  const handleSearchChange = (e) => {
    const val = e.target.value;
    searchRef.current = val;
    setSearch(val);
    debouncedFetch(buildAllParams({ page: 1, ...(val ? { [SEARCH_FIELD]: val } : {}) }));
  };

  const handleClearSearch = () => {
    searchRef.current = '';
    setSearch('');
    setPage(1);
    fetchData(buildAllParams({ page: 1 }));
  };

  const handleSort = useCallback(({ colId, sort: dir }) => {
    const newSort = dir ? { attribute: colId, direction: dir } : { attribute: 'id', direction: 'desc' };
    sortRef.current = newSort;
    setSort(newSort);
    setPage(1);
    fetchData(buildAllParams({ page: 1, sort: newSort }));
  }, [fetchData, buildAllParams]);

  const handleCreatedDateChange = (range) => {
    quickCreatedRef.current = range;
    setQuickCreatedDate(range);
    setPage(1);
    fetchData({
      ...buildBase({ page: 1 }),
      ...buildCreatedDateParam(range),
      ...buildUnitsParam(quickUnitsRef.current),
      ...buildPifTypeParam(quickPifTypeRef.current),
    });
  };

  const handleSalesUnitChange = (ids) => {
    quickUnitsRef.current = ids;
    setQuickSalesUnitIds(ids);
    setPage(1);
    fetchData({
      ...buildBase({ page: 1 }),
      ...buildCreatedDateParam(quickCreatedRef.current),
      ...buildUnitsParam(ids),
      ...buildPifTypeParam(quickPifTypeRef.current),
    });
  };

  const handlePifTypeChange = (ids) => {
    quickPifTypeRef.current = ids;
    setQuickPifTypeIds(ids);
    setPage(1);
    fetchData({
      ...buildBase({ page: 1 }),
      ...buildCreatedDateParam(quickCreatedRef.current),
      ...buildUnitsParam(quickUnitsRef.current),
      ...buildPifTypeParam(ids),
    });
  };

  const handleApplyFilter = (params, tags) => {
    activeFilterParamsRef.current = params;
    dynamicPayloadRef.current = buildDynamicPayload(dynRows);
    setFilterTags([...tags, ...buildDynamicTags(dynRows)]);
    setPage(1);
    setShowFilter(false);
    fetchData(buildAllParams({ page: 1, ...params }));
  };

  const handleResetFilter = () => {
    activeFilterParamsRef.current = {};
    dynamicPayloadRef.current = { q: {}, area_conditions: [] };
    setFilterValues(getEmptyValues(FILTER_FIELDS));
    setDynRows([]);
    setFilterTags([]);
    setPage(1);
    fetchData(buildAllParams({ page: 1 }));
  };

  const removeTag = (tag) => {
    // Dynamic precise condition (record `q` or area JSONB — identified by row tag id)
    const isDynamicTag = dynRows.some((r) => dynamicRowTagId(r) === tag.id);
    if (isDynamicTag) {
      const nextRows = dynRows.filter((r) => dynamicRowTagId(r) !== tag.id);
      setDynRows(nextRows);
      dynamicPayloadRef.current = buildDynamicPayload(nextRows);
      setFilterTags((prev) => prev.filter((t) => t.id !== tag.id));
      setPage(1);
      fetchData(buildAllParams({ page: 1 }));
      return;
    }

    // Drawer-field param (top-level)
    const newParams = Object.fromEntries(
      Object.entries(activeFilterParamsRef.current).filter(([k]) => k !== tag.id)
    );
    activeFilterParamsRef.current = newParams;
    setFilterTags((prev) => prev.filter((t) => t.id !== tag.id));
    setPage(1);
    fetchData(buildAllParams({ page: 1, ...newParams }));
  };

  const activeFilterCount = useMemo(() => filterTags.length, [filterTags]);

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* Header */}
      <div className="tw-mb-1">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
            <div className="tw-flex tw-items-center tw-gap-2">
              <MdDescription className="tw-text-blue-600" size={22} />
              <div>
                <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">PIF Report</h1>
                <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Product Information Forms across projects — specs, weights &amp; areas per building.</p>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              type="button"
              onClick={() => fetchData(buildAllParams())}
              disabled={loading}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer disabled:tw-opacity-50"
            >
              <FaSyncAlt size={12} className={loading ? 'tw-animate-spin' : ''} />
              Reload
            </button>
            <button
              type="button"
              onClick={() => dispatch(exportPIFs(buildAllParams()))}
              disabled={exporting || loading}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-green-300 tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100 tw-text-sm tw-font-medium tw-cursor-pointer disabled:tw-opacity-50"
            >
              <FaFileExcel size={13} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">

          {/* Search */}
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-44 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Q#, J#, project name..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
            />
            {search && (
              <button type="button" onClick={handleClearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Created Date (default: this year) */}
          <DateRangePicker
            label="Created Date"
            startDate={quickCreatedDate.startDate}
            endDate={quickCreatedDate.endDate}
            onChange={handleCreatedDateChange}
          />

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Sales Unit */}
          <SalesUnitGroupedDropdown
            units={activeSalesUnitsData}
            selectedIds={quickSalesUnitIds}
            onChange={handleSalesUnitChange}
            placeholder="All Units"
            width={220}
          />

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* PIF Type */}
          <GroupedCheckboxDropdown
            groups={PIF_TYPE_GROUPS}
            selectedIds={quickPifTypeIds}
            onChange={handlePifTypeChange}
            placeholder="PIF Type"
            width={180}
            showGroupLabels={false}
          />

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* More Filters */}
          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
              activeFilterCount > 0
                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700'
                : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'
            }`}
          >
            <MdFilterAlt size={15} />
            More Filters
            {activeFilterCount > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">
                {activeFilterCount}
              </span>
            )}
          </button>

          {!loading && totalRows > 0 && (
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
              {totalRows.toLocaleString()} records
            </span>
          )}
        </div>

        {/* Active filter tags (drawer only) */}
        {filterTags.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-primary-50/40">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-flex-shrink-0">Active filters:</span>
            {filterTags.map((tag) => (
              <span key={tag.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-1 tw-py-0.5 tw-bg-white tw-border tw-border-primary-200 tw-text-slate-700 tw-rounded-md tw-text-xs tw-shadow-sm">
                <span className="tw-text-primary-500 tw-font-semibold">{tag.name}</span>
                <span className="tw-text-slate-300 tw-mx-0.5">·</span>
                <span className="tw-max-w-[140px] tw-truncate tw-text-slate-600">{Array.isArray(tag.value) ? tag.value.join(' – ') : tag.value}</span>
                <button type="button" onClick={() => removeTag(tag)} className="tw-ml-0.5 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors">
                  <FaTimes size={8} />
                </button>
              </span>
            ))}
            <button type="button" onClick={handleResetFilter} className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-md tw-text-xs tw-text-red-500 hover:tw-bg-red-500 hover:tw-text-white tw-border tw-border-red-200 hover:tw-border-red-500 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-font-medium">
              <FaTimes size={8} />
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <AgGridTable
          rowData={data}
          columnDefs={COLUMN_DEFS}
          loading={loading}
          pagination={false}
          filter={false}
          height="max(320px, calc(100dvh - 310px))"
          onSortChanged={handleSort}
          colDefOverride={{ sortable: false }}
        />
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalRows}
        perPage={perPage}
        pageSizeOptions={[20, 50, 100, 200]}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
        disabled={loading}
      />

      {/* Advanced filter drawer */}
      <AdvanceFilterDrawer
        show={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={handleApplyFilter}
        onReset={handleResetFilter}
        fields={FILTER_FIELDS}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        title="Filter PIFs"
        description="Build precise conditions on record fields or Area (BDS) spec values"
        dividerAfter={DIVIDER_AFTER}
        extraActiveCount={buildDynamicTags(dynRows).length}
      >
        <DynamicFilters value={dynRows} onChange={setDynRows} />
      </AdvanceFilterDrawer>
    </div>
  );
};

export default PIFPage;
